// libs
import styled, { css } from 'styled-components'
// themes
import { colors } from 'themes/index'
// components
import { Chip as ChipOrigin } from 'components/UI'
// icons
import CopyIconOrigin from '@mui/icons-material/ContentCopy'
import DragIconOrigin from '@mui/icons-material/DragHandle'
// views
import { ButtonsWrapper as BWrapper } from 'components/PageComponents/Settings/ObjectsAndProperties/Edit/Properties/components/Property/view'
// utils
import { darken } from 'polished'

const AddButton = styled('div')<{ $disabled?: boolean }>`
  && {
    color: ${colors.blue500};
    cursor: pointer;
    display: inline-block;
    font-size: 16px;
    font-weight: 500;
    margin-top: 8px;

    ${({ $disabled }) =>
      $disabled &&
      css`
        pointer-events: none;
        color: ${colors.gray280};
      `};

    &:hover {
      color: ${({ $disabled }) => darken(0.1, $disabled ? colors.gray280 : colors.blue500)};
    }
  }
`

const ButtonsWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;

  div:first-child {
    margin-right: 8px;
  }
`

const DragIcon = styled(DragIconOrigin)`
  width: 24px;
`

const InnerWrapper = styled('div')<{ isFormDirty: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  ${({ isFormDirty }) =>
    isFormDirty &&
    css`
      margin-bottom: 56px;
    `}
`

const IconWrapper = styled('div')`
  align-items: center;
  border-radius: 4px 0 0 4px;
  display: flex;
  height: inherit;
  margin-right: 8px;
`

const OptionContent = styled('div')<{ isActive?: boolean }>`
  overflow: hidden;
  align-items: center;
  background-color: ${colors.gray190};
  border-radius: 4px;
  color: ${({ isActive }) => (isActive ? 'unset' : colors.gray350)};
  display: flex;
  flex: 0.6;
  height: 50px;
  padding: 8px;

  span {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
`

const OptionWrapper = styled('div')<{ isDisabled?: boolean }>`
  border-radius: 4px;
  display: flex;
  height: 54px;
  position: relative;
  border: 2px solid transparent;

  &:hover {
    border-color: ${({ isDisabled }) => (isDisabled ? 'transparent' : colors.blue500)};

    ${BWrapper} {
      display: ${({ isDisabled }) => (isDisabled ? 'none' : 'flex')};
    }
  }

  > div:first-child {
    flex: 1;
    margin-right: 8px;
  }

  ${BWrapper} {
    display: none;
  }
`

const SwitchContainer = styled('div')`
  display: flex;
  align-items: center;
  font-size: 16px;

  & > div:first-child {
    width: initial;
  }
`

const Wrapper = styled('div')`
  margin-bottom: 8px;
`

const CopyIcon = styled(CopyIconOrigin)`
  height: 20px;
  width: 20px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const Chip = styled(ChipOrigin)`
  && {
    color: darkgrey;
    font-weight: 500;
    margin-left: 8px;
  }
`

const ChipContainer = styled('div')`
  margin-left: auto;
`

// native scroll instead of perfect-scrollbar: its `overflow: hidden` container is not detected by react-beautiful-dnd,
// and the options list (virtualized against this container) needs a scroll parent visible to dnd auto-scroll
const ScrollContainer = styled('div')`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export {
  AddButton,
  ButtonsWrapper,
  CopyIcon,
  InnerWrapper,
  IconWrapper,
  OptionContent,
  OptionWrapper,
  SwitchContainer,
  Wrapper,
  DragIcon,
  Chip,
  ChipContainer,
  ScrollContainer
}
